export const SPOTLIGHT_COPY = {
  actions: {
    exclude: {
      label: 'Exclude from auto',
      desc: 'Opt this listing out of the optimizer.',
    },
    include: {
      label: 'Include in auto',
      desc: 'Let the optimizer place this listing.',
    },
    pin: { label: 'Pin Spotlight', desc: 'Lock a license to this listing.' },
    pinWhenFull: { desc: 'Replace an existing pinned listing' },
    unpin: { label: 'Unpin', desc: 'Return the license to auto-optimize.' },
  },
  // Fallback only — the package name is per-listing (context/fixture today, advertising API once RECE-1569 wires it).
  packageNameFallback: 'Monthly Flat Fee',
  product: {
    spotlight: 'Spotlight',
  },
  smartAssign: {
    toggleLabel: 'Smart Assign',
    tooltip: 'Smart Assign fills your unused Spotlight placements automatically, and reassigns them as listings rent.',
    // Shown when the customer only has feed-managed listings (hasZramManageableListings === false), which blocks the toggle.
    feedManagedTooltip: 'Smart Assign for feed-managed listings is on its way — check back soon!',
    // Activation (off-to-on) modal, RECE-1889. Copy is placeholder pending Netza's final lock.
    modal: {
      title: 'Smart Assign',
      description: {
        lead: 'Smart Assign automatically fills your unused Spotlight placements with ',
        bold: 'the listings that have been on the market longest',
        tail: ', and reassigns them as listings rent.',
      },
      selectLabel: 'Select an option',
      allIncluded: {
        tag: 'Fastest',
        title: 'All eligible listings',
        desc: 'Smart Assign fills all your placements right away.',
        footnote: 'New listings are considered for Smart Assign automatically.',
      },
      allExcluded: {
        tag: 'Most control',
        title: 'I’ll select listings later',
        desc: 'Nothing is added yet — pick the listings Smart Assign can use from your list.',
        footnote: 'New listings stay excluded until you add them.',
      },
      confirm: 'Confirm',
      cancel: 'Cancel',
      savedToast: 'Smart Assign is on.',
      errorToast: 'Smart Assign could not be turned on. Try again.',
    },
  },
  disableModal: {
    title: 'Turn off Smart Assign?',
    description:
      'You’ll apply Listing Spotlight to each listing yourself. You can turn Smart Assign back on at any time, but your previous selections won’t be restored.',
    confirm: 'Turn off Smart Assign',
    cancel: 'Cancel',
  },
  reassignModal: {
    title: 'Reassign Spotlight',
    description:
      'All Spotlight placements are currently pinned. Select which pinned property to replace with “{candidate}”.',
    comboboxLabel: 'Replace this property',
    comboboxPlaceholder: 'Search properties...',
    // Deliberately does not name a cause: the endpoint returns an empty list both when nothing is pinned
    // and when the placement lookup failed upstream, so the copy has to hold for a retry and a dead end.
    empty:
      'We couldn’t find any properties to replace. Try again in a moment, or contact support if this keeps happening.',
    confirm: 'Reassign Spotlight',
    cancel: 'Cancel',
  },
  // Shared across the panel header tooltip and the SpolightListingsBanner upsell body — keep in sync, don't fork.
  exposureCopy:
    'Listing Spotlight gives single-family listings on average 2x more exposure than Feed Connect listings, according to Zillow internal data.',
  tooltips: {
    optimizeBy: 'Automatically optimizes which listings get your available Spotlight placements.',
    summaryCounts:
      'Pinned placements stay put; Auto placements are assigned by the optimizer. Available spots are unused licenses.',
  },
  // New Smart Assign per-listing cell (RECE-1963). Same 3 options in both config modes; endpoints differ by mode.
  smartAssignCell: {
    state: {
      pinned: 'Pinned',
      smartAssign: 'Smart Assign',
      excluded: 'Excluded',
      // ALL_EXCLUDED default: a listing not opted in reads as removed from Smart Assign, not a hard exclusion.
      removed: 'Removed',
    },
    actions: {
      pin: { label: 'Pinned', desc: 'Always spotlighted; uses one placement.' },
      smartAssign: { label: 'Smart Assign', desc: 'May be spotlighted, based on your Optimize by setting.' },
      exclude: { label: 'Excluded', desc: 'Skipped by Smart Assign.' },
      // ALL_EXCLUDED equivalent of exclude: the listing is simply taken back out of Smart Assign.
      remove: { label: 'Remove Smart Assign', desc: 'Returns the listing to its default excluded state.' },
    },
    // Smart Assign off: manual pin/remove only.
    legacy: {
      pinnedLabel: 'Listing Spotlight',
      eligibleLabel: 'Eligible for Spotlight',
      apply: { label: 'Apply Listing Spotlight', desc: 'Add Spotlight to this listing.' },
      remove: { label: 'Remove Listing Spotlight', desc: 'Remove Spotlight from this listing.' },
    },
  },
  // Placement-state labels converged at the 06-16 Pod Sync (spec for RECE-1508). Final copy is Netza's to lock.
  state: {
    eligible: 'Eligible for Spotlight',
    excluded: 'Auto-spotlight excluded',
    manageInFeed: 'Manage Spotlight in feed',
    notEligible: 'Not eligible for Spotlight',
    pinned: 'Pinned',
    spotlighted: 'Auto-spotlight',
  },
} as const;
